"use client";

import { useSyncExternalStore } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSelector } from "react-redux";
import { CircleAlert, CirclePause, Gauge, X, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { cn } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { fetchData } from "@/utils/api-handler";

/** Orange at or below this share of the cycle left. */
const CREDIT_WARN_PCT = 20;
/** Pink at or below this share of the cycle left. */
const CREDIT_CRITICAL_PCT = 10;

const DISMISS_KEY = "aios-credit-banner-dismissed-v1";
const DISMISS_EVENT = "aios-credit-banner-dismissed";

type CreditLevel = "ok" | "warn" | "critical" | "empty";

export function creditStatus(available: number, allocated: number): {
  pctRemaining: number;
  level: CreditLevel;
} {
  if (available <= 0) {
    return { pctRemaining: 0, level: "empty" };
  }
  // Nothing was allocated this cycle, so there is no share to divide.
  if (allocated === 0) {
    return { pctRemaining: 100, level: "ok" };
  }
  const pctRemaining = Math.min(
    100,
    Math.max(0, Math.round((available / allocated) * 100)),
  );
  const level: CreditLevel =
    pctRemaining > CREDIT_WARN_PCT
      ? "ok"
      : pctRemaining > CREDIT_CRITICAL_PCT
        ? "warn"
        : "critical";
  return { pctRemaining, level };
}

function hiddenOnThisRoute(pathname: string) {
  return (
    pathname.startsWith("/iai-academy") || pathname.startsWith("/super-admin")
  );
}

function useCreditStatus() {
  const pathname = usePathname();
  const hidden = hiddenOnThisRoute(pathname);
  const walletId = useSelector(
    (state: RootState) => state.user.user?.organization?.billing_wallet?.id,
  );
  const { data, hasPermission, isSuccess } = useProtectedQuery<{
    available_credits?: number;
    stats?: { total_credits_allocated?: number };
  }>({
    queryKey: ["wallet", walletId],
    queryFn: () => fetchData(`/api/billing/wallets/${walletId}`),
    requiredPermissions: ["VIEW_BILLING_WALLET"],
    enabled: Boolean(walletId) && !hidden,
  });

  if (hidden || !walletId || !hasPermission || !isSuccess || !data) return null;

  return creditStatus(
    data.available_credits ?? 0,
    data.stats?.total_credits_allocated ?? 0,
  );
}

function readDismissed(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

function useDismissedLevel() {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener(DISMISS_EVENT, onChange);
      return () => window.removeEventListener(DISMISS_EVENT, onChange);
    },
    readDismissed,
    () => null,
  );
}

function dismissLevel(level: CreditLevel) {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, level);
  } catch {
    // The banner just stays for this page view.
  }
  window.dispatchEvent(new Event(DISMISS_EVENT));
}

export function NavUsage() {
  const status = useCreditStatus();
  if (!status) return null;

  const { pctRemaining, level } = status;
  const label = level === "empty" ? "No credits" : `${pctRemaining}% left`;
  const tone = cn(
    level === "warn" && "text-iai-orange",
    (level === "critical" || level === "empty") && "text-iai-pink",
  );
  const wash = cn(
    level === "warn" &&
      "bg-[#FFCD18]/10 hover:bg-[#FFCD18]/20 dark:bg-[#FFCD18]/10 dark:hover:bg-[#FFCD18]/15",
    (level === "critical" || level === "empty") &&
      "bg-[#FA3387]/10 hover:bg-[#FA3387]/15 dark:bg-[#FA3387]/15 dark:hover:bg-[#FA3387]/20",
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip={`Usage · ${label}`} className={wash}>
          <Link href="/billing?tab=billing">
            <Gauge className={cn(level !== "ok" && tone)} />
            <span>Usage</span>
            <span
              className={cn(
                "ml-auto text-xs tabular-nums",
                level === "ok" ? "text-muted-foreground" : cn("font-medium", tone),
              )}
            >
              {label}
            </span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function CreditBanner() {
  const status = useCreditStatus();
  const dismissedLevel = useDismissedLevel();
  if (!status) return null;

  const { level } = status;
  if ((level !== "critical" && level !== "empty") || dismissedLevel === level) {
    return null;
  }

  const empty = level === "empty";
  const Icon = empty ? CirclePause : CircleAlert;

  return (
    <div
      role="alert"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-[#FA3387]/30 bg-gradient-to-r from-[#FA3387]/10 to-[#FA3387]/5 px-4 py-2 text-foreground dark:from-[#FA3387]/20 dark:to-[#FA3387]/10 min-[396px]:flex-nowrap min-[396px]:py-2.5 sm:px-6"
    >
      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#FA3387]/15 text-iai-pink">
        <Icon className="size-3.5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1 basis-[12rem]">
        <p className="text-sm font-semibold leading-snug">
          {empty ? "No credits left" : "Your Workforce will pause soon"}
        </p>
        <p className="mt-0.5 text-xs leading-snug text-foreground/80 min-[396px]:leading-relaxed">
          {empty
            ? "Your Workforce is paused. Top up your credits to get it working again."
            : "Your credits are running low. Top up now to keep Workforce running."}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1 max-[395px]:basis-full max-[395px]:justify-end max-[395px]:pl-9">
        <Button
          asChild
          variant="outline"
          size="sm"
          className="h-8 border-[#FA3387]/40 bg-background/90 px-3 text-xs font-semibold text-iai-navy hover:bg-background hover:text-iai-purple"
        >
          <Link href="/billing?tab=credits&panel=auto-topup">
            <Zap className="size-3.5" />
            Top up credits
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => dismissLevel(level)}
          aria-label="Dismiss"
          className="size-8 text-foreground/60 hover:bg-[#FA3387]/10 hover:text-foreground"
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}
